import type { Product, SubcategoryPage } from './types';
import { Texts } from '~/texts/runtime';

/**
 * A "Produktové kategorie" mega-menu item: one of Flexi's subcategoryPages
 * (minus `application-recommendation`), mapped to the matching Flexi + Mercury
 * subcategoryPage id(s) so a single click can list both lines' products.
 * Mercury's slugs mostly mirror Flexi's exactly; `keypads-and-card-readers` is
 * an exception reconciled by id below (see spec's "Slug reconciliation"
 * section). Mercury's former sirens duplicate (`2_4`) was merged into `2_7`.
 * `software-and-mobile-apps` (Flexi `1_16` / Mercury `2_18`) was removed
 * entirely, on both lines, so it's dropped from this list too.
 */
export type CanonicalCategory = {
  slug: string;
  flexiSubcategoryIds: string[];
  mercurySubcategoryIds: string[];
};

export const CANONICAL_CATEGORIES: CanonicalCategory[] = [
  { slug: 'control-panels', flexiSubcategoryIds: ['1_0'], mercurySubcategoryIds: ['2_0'] },
  { slug: 'keypads-and-card-readers', flexiSubcategoryIds: ['1_1'], mercurySubcategoryIds: ['2_1'] },
  { slug: 'remote-controls', flexiSubcategoryIds: ['1_2'], mercurySubcategoryIds: ['2_2'] },
  { slug: 'detectors', flexiSubcategoryIds: ['1_3'], mercurySubcategoryIds: ['2_3'] },
  { slug: 'panic-buttons', flexiSubcategoryIds: ['1_4'], mercurySubcategoryIds: ['2_6'] },
  { slug: 'sirens-and-indicators', flexiSubcategoryIds: ['1_5'], mercurySubcategoryIds: ['2_7'] },
  { slug: 'ip-cameras', flexiSubcategoryIds: ['1_6'], mercurySubcategoryIds: ['2_8'] },
  { slug: 'output-modules-and-relays', flexiSubcategoryIds: ['1_7'], mercurySubcategoryIds: ['2_9'] },
  {
    slug: 'temperature-and-ventilation-control',
    flexiSubcategoryIds: ['1_8'],
    mercurySubcategoryIds: ['2_10'],
  },
  { slug: 'blind-and-shutter-control', flexiSubcategoryIds: ['1_9'], mercurySubcategoryIds: ['2_11'] },
  { slug: 'consumption-metering', flexiSubcategoryIds: ['1_10'], mercurySubcategoryIds: ['2_12'] },
  {
    slug: 'digital-bus-cables-and-accessories',
    flexiSubcategoryIds: ['1_11'],
    mercurySubcategoryIds: ['2_13'],
  },
  { slug: 'extending-wireless-range', flexiSubcategoryIds: ['1_12'], mercurySubcategoryIds: ['2_14'] },
  { slug: 'accumulators-and-batteries', flexiSubcategoryIds: ['1_13'], mercurySubcategoryIds: ['2_15'] },
  { slug: 'installation-accessories', flexiSubcategoryIds: ['1_14'], mercurySubcategoryIds: ['2_16'] },
  { slug: 'auxiliary-power-supplies', flexiSubcategoryIds: ['1_15'], mercurySubcategoryIds: ['2_17'] },
];

export function getCanonicalCategory(slug: string): CanonicalCategory | undefined {
  return CANONICAL_CATEGORIES.find((category) => category.slug === slug);
}

/**
 * A few canonical categories' underlying Flexi subcategoryPage title is line-specific
 * (e.g. "Flexi control panels") even though the canonical category is combined and
 * line-agnostic — override those with a generic label instead of showing "Flexi" here.
 */
const CANONICAL_CATEGORY_TITLE_OVERRIDES: Record<string, string> = {
  'control-panels': Texts.MegaMenuCategoryControlPanels,
};

/** Translation-key title for a canonical category, e.g. for mega-menu links and the Kategorie filter. */
export function canonicalCategoryTitle(category: CanonicalCategory, subcategoryPages: SubcategoryPage[]): string {
  const flexiPage = subcategoryPages.find((page) => page.id === category.flexiSubcategoryIds[0]);

  return CANONICAL_CATEGORY_TITLE_OVERRIDES[category.slug] ?? flexiPage?.title ?? category.slug;
}

/** Combined Flexi+Mercury product list for a canonical category, deduplicated by product id. */
export function combinedCategoryProducts(products: Product[], categorySlug: string): Product[] {
  const category = getCanonicalCategory(categorySlug);
  if (!category) {
    return [];
  }
  const ids = new Set([...category.flexiSubcategoryIds, ...category.mercurySubcategoryIds]);
  const seen = new Set<string>();
  const result: Product[] = [];
  for (const product of products) {
    if (seen.has(product.id)) {
      continue;
    }
    if (product.subcategoryIds.some((id) => ids.has(id))) {
      seen.add(product.id);
      result.push(product);
    }
  }

  return result;
}

/**
 * Product list for a /products?category= request: combined Flexi+Mercury for
 * the selected categories (OR-matched, deduplicated by product id); no
 * recognized category slug falls back to the full, unfiltered `products` list.
 */
export function filterByCategory(products: Product[], categorySlugs: string[]): Product[] {
  const known = categorySlugs.filter((slug) => getCanonicalCategory(slug));
  if (known.length === 0) {
    return products;
  }

  const seen = new Set<string>();
  const result: Product[] = [];
  for (const slug of known) {
    for (const product of combinedCategoryProducts(products, slug)) {
      if (!seen.has(product.id)) {
        seen.add(product.id);
        result.push(product);
      }
    }
  }

  return result;
}
